import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogHeader } from "../components/ui/dialog";
import {
  Trophy,
  MessageCircle,
  FileSignature,
  Sparkles,
  Layers,
  Layout as LayoutIcon,
  ShieldCheck,
  Check,
  ArrowRight,
  Star,
  ChevronDown,
  Phone,
  Mail,
  Send,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import AnimatedWizardDemo from "../components/landing/AnimatedWizardDemo";

const FEATURES = [
  {
    icon: MessageCircle,
    title: "Chat famiglie ↔ società",
    text: "Messaggi in tempo reale con genitori e staff. Gruppi per squadra, annunci dello staff, DM 1-a-1. Push e email.",
    color: "from-sky-500 to-blue-600",
  },
  {
    icon: FileSignature,
    title: "Modulistica + Firma OTP",
    text: "Iscrizioni online con firma digitale via codice email. Cartellini FIGC, distinta gara, certificati fiscali pronti in PDF.",
    color: "from-amber-500 to-orange-600",
  },
  {
    icon: Sparkles,
    title: "Mister AI",
    text: "Schede allenamento, analisi post-match, suggerimenti convocazione, messaggi per genitori — generati in secondi.",
    color: "from-fuchsia-500 to-purple-600",
  },
  {
    icon: Layers,
    title: "Multi-squadra reale",
    text: "Gestisci tutte le categorie del settore giovanile da un solo account: campi condivisi, calendario coordinato, dati fiscali centralizzati.",
    color: "from-emerald-500 to-green-600",
  },
  {
    icon: LayoutIcon,
    title: "Lavagna tattica animata",
    text: "Timeline multi-frame, frecce, esercizi, esporta GIF animate. Mostra gli schemi prima del fischio.",
    color: "from-rose-500 to-red-600",
  },
  {
    icon: ShieldCheck,
    title: "Conformità FIGC",
    text: "Distinta gara ufficiale, tessera SGS, dati legali ASD/SSD, certificati medici tracciati. Tutto pronto per il delegato.",
    color: "from-cyan-500 to-teal-600",
  },
];

const FAQS = [
  {
    q: "Devo inserire la carta di credito per la prova?",
    a: "No. Ogni squadra ha 14 giorni di prova gratuita del piano Pro senza carta richiesta. Al termine la squadra torna sul piano Free (gratis per sempre, 1 squadra fino a 30 giocatori).",
  },
  {
    q: "Posso gestire più categorie giovanili?",
    a: "Sì. Il piano Pro include fino a 3 squadre, il piano Club è illimitato. I campi e i dati fiscali sono condivisi automaticamente tra le squadre dello stesso club.",
  },
  {
    q: "I genitori vedono i pagamenti delle quote?",
    a: "I genitori hanno una dashboard dedicata, di sola lettura, con il calendario partite del figlio, le statistiche e i video Veo. Le quote restano visibili solo a Mister e dirigenti.",
  },
  {
    q: "Le firme OTP via email sono valide legalmente?",
    a: "Sono firme elettroniche semplici secondo eIDAS (Reg. UE 910/2014), tracciate con audit log (timestamp UTC, IP, ID firma) — adatte per moduli iscrizione, liberatorie e privacy.",
  },
  {
    q: "Posso usare l'app sul telefono?",
    a: "Sì, è una Progressive Web App installabile su Android (prompt nativo) e iOS (Aggiungi alla schermata Home). Notifiche push incluse.",
  },
  {
    q: "I dati sono al sicuro?",
    a: "Hosting europeo, dati cifrati in transito (HTTPS) e a riposo, autenticazione con cookie httpOnly, brute-force lockout. GDPR-compliant.",
  },
];

const TESTIMONIALS = [
  {
    name: "Mister Andrea",
    role: "ASD Aurora Calcio · Pulcini 2015",
    quote:
      "Prima passavo le domeniche a fare distinte e WhatsApp. Ora la convocazione parte in 30 secondi e la chat famiglie ha sostituito 3 gruppi.",
    rating: 5,
  },
  {
    name: "Dirigente Stefano",
    role: "SSD San Giorgio · Settore Giovanile",
    quote:
      "Iscrizioni online con firma OTP: 47 famiglie iscritte in una settimana, zero moduli cartacei smarriti. Game changer per la segreteria.",
    rating: 5,
  },
  {
    name: "Mister Laura",
    role: "ASD Junior · Allievi Femminile",
    quote:
      "Mister AI mi prepara la seduta in 2 minuti. La lavagna tattica con GIF la mostro alle ragazze nello spogliatoio prima della partita.",
    rating: 5,
  },
];

const PLANS = [
  {
    key: "free",
    name: "Free",
    monthly: { price: "€0", period: "per sempre", note: null },
    yearly: { price: "€0", period: "per sempre", note: null },
    tagline: "Per chi inizia",
    bullets: ["1 squadra", "Fino a 30 giocatori", "Convocazioni, presenze, allenamenti", "Distinta gara FIGC"],
    cta: "Inizia gratis",
    highlight: false,
  },
  {
    key: "pro",
    name: "Pro",
    monthly: { price: "€19,90", period: "/mese", note: null },
    yearly: { price: "€191", period: "/anno", note: "≈ €15,90/mese · risparmi 20%" },
    tagline: "Per il Mister ambizioso",
    bullets: [
      "Fino a 3 squadre · 120 giocatori",
      "Mister AI + Modulistica completa",
      "Firma OTP + Cartellini FIGC",
      "Chat famiglie · Push notifications",
      "14 giorni di prova gratis",
    ],
    cta: "Prova 14 giorni gratis",
    highlight: true,
  },
  {
    key: "club",
    name: "Club",
    monthly: { price: "€49,90", period: "/mese", note: null },
    yearly: { price: "€479", period: "/anno", note: "≈ €39,90/mese · risparmi 20%" },
    tagline: "Per società strutturate",
    bullets: [
      "Squadre illimitate",
      "Sito pubblico società + sponsor",
      "Gestione spese · magazzino",
      "Tornei · QR presenze",
      "Supporto prioritario",
    ],
    cta: "Scegli Club",
    highlight: false,
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [billingCycle, setBillingCycle] = useState("yearly"); // "monthly" | "yearly"

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-slate-950/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="h-9 w-9 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Trophy size={18} className="text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-base leading-none">Calcio Club</div>
              <div className="text-[10px] text-slate-400 leading-none mt-0.5">Manager</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <a href="#features" className="hover:text-white transition">Funzionalità</a>
            <a href="#pricing" className="hover:text-white transition">Prezzi</a>
            <a href="#faq" className="hover:text-white transition">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-white/5 h-9"
                data-testid="landing-nav-login"
              >
                Accedi
              </Button>
            </Link>
            <Button
              onClick={() => navigate("/login?mode=register")}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 h-9 font-semibold"
              data-testid="landing-nav-signup"
            >
              Prova gratis
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-16 pb-24 px-5 overflow-hidden">
        {/* Decorative orbs */}
        <div className="absolute top-20 -left-32 h-96 w-96 rounded-full bg-emerald-500/20 blur-3xl animate-orb pointer-events-none" />
        <div
          className="absolute top-40 -right-32 h-96 w-96 rounded-full bg-sky-500/20 blur-3xl animate-orb pointer-events-none"
          style={{ animationDelay: "2s" }}
        />
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-medium mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Nuovo · Setup guidato in 2 minuti
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Lo <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-sky-300 bg-clip-text text-transparent">spogliatoio digitale</span><br />
            del calcio giovanile italiano.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Anagrafica, convocazioni, distinta FIGC, allenamenti, chat famiglie,
            Mister AI e firma OTP — tutto in un&apos;unica piattaforma pensata
            per Mister, dirigenti e settori giovanili.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={() => navigate("/login?mode=register")}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 h-12 px-7 text-base font-semibold shadow-[0_0_30px_-8px_rgba(16,185,129,0.5)]"
              data-testid="landing-hero-cta-trial"
            >
              Prova gratis 30 giorni
              <ArrowRight size={18} className="ml-1.5" />
            </Button>
            <Button
              onClick={() => setDemoOpen(true)}
              size="lg"
              variant="outline"
              className="h-12 px-7 text-base font-semibold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              data-testid="landing-hero-cta-demo"
            >
              Richiedi demo
            </Button>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Nessuna carta richiesta · 14 giorni Pro inclusi · Annulli in 1 click
          </p>

          {/* Wizard Demo */}
          <div className="mt-16">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">
              Setup guidato — anteprima live
            </div>
            <AnimatedWizardDemo />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="relative py-8 border-y border-white/5 bg-slate-900/40 overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 text-center text-xs uppercase tracking-widest text-slate-500 mb-4">
          Già in uso da società di Pulcini, Esordienti, Giovanissimi e Allievi
        </div>
        <div className="relative overflow-hidden">
          <div className="flex gap-12 animate-marquee whitespace-nowrap text-slate-400 font-heading font-bold text-sm">
            {Array.from({ length: 2 }).flatMap((_, k) =>
              ["ASD Aurora", "SSD San Giorgio", "ASD Junior 2010", "Pol. Comunale", "ASD Star Calcio", "SSD Bianconeri", "ASD Falchi 2014", "Polisportiva Centro"].map(
                (n) => (
                  <span key={`${k}-${n}`} className="inline-flex items-center gap-2">
                    <Trophy size={14} className="text-emerald-400/70" />
                    {n}
                  </span>
                )
              )
            )}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 px-5 relative">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">
              Tutto quello che serve
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight mb-4">
              Una sola piattaforma. Sei superpoteri.
            </h2>
            <p className="text-slate-400">
              Pensata insieme a Mister di società dilettantistiche e settori
              giovanili. Niente fronzoli, solo gli strumenti che fanno la
              differenza prima, durante e dopo la partita.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="group relative p-6 rounded-2xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/10 transition"
                data-testid={`landing-feature-${f.title.toLowerCase().split(" ")[0]}`}
              >
                <div
                  className={`h-11 w-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 shadow-lg`}
                >
                  <f.icon size={20} className="text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 px-5 bg-gradient-to-b from-slate-950 via-slate-900/40 to-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">
              Voci dal campo
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">
              Mister e dirigenti che hanno già scelto.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                className="p-6 rounded-2xl bg-white/[0.04] border border-white/[0.06]"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="text-sm font-semibold">{t.name}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">
              Piani semplici
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Inizia gratis. Cresci quando vuoi.
            </h2>
            <p className="text-slate-400">
              Cambia o annulla in qualsiasi momento. Niente vincoli, niente
              clausole nascoste.
            </p>
          </div>

          {/* Billing cycle toggle */}
          <div className="flex justify-center mb-10">
            <div
              className="inline-flex items-center rounded-full bg-white/[0.05] border border-white/[0.08] p-1"
              data-testid="billing-cycle-toggle"
            >
              <button
                onClick={() => setBillingCycle("monthly")}
                data-testid="cycle-monthly-btn"
                className={`px-5 py-2 text-sm font-semibold rounded-full transition ${
                  billingCycle === "monthly"
                    ? "bg-white text-slate-950"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                Mensile
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                data-testid="cycle-yearly-btn"
                className={`px-5 py-2 text-sm font-semibold rounded-full transition flex items-center gap-2 ${
                  billingCycle === "yearly"
                    ? "bg-white text-slate-950"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                Annuale
                <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500 text-slate-950">
                  -20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map((p) => {
              const cycle = p[billingCycle] || p.monthly;
              return (
                <div
                  key={p.key}
                  className={`relative p-7 rounded-2xl border ${
                    p.highlight
                      ? "bg-gradient-to-b from-emerald-500/10 to-emerald-500/[0.02] border-emerald-500/40 shadow-[0_0_60px_-30px_rgba(16,185,129,0.6)]"
                      : "bg-white/[0.04] border-white/[0.06]"
                  }`}
                  data-testid={`landing-plan-${p.key}`}
                >
                  {p.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-bold uppercase tracking-widest">
                      Più scelto
                    </div>
                  )}
                  <div className="text-xs text-slate-400 uppercase tracking-widest mb-1">
                    {p.tagline}
                  </div>
                  <div className="font-heading text-2xl font-bold mb-1">{p.name}</div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span
                      className="font-heading text-4xl font-bold"
                      data-testid={`landing-plan-price-${p.key}`}
                    >
                      {cycle.price}
                    </span>
                    <span className="text-sm text-slate-400">{cycle.period}</span>
                  </div>
                  {cycle.note && (
                    <div className="text-xs text-emerald-400 mb-4" data-testid={`landing-plan-note-${p.key}`}>
                      {cycle.note}
                    </div>
                  )}
                  {!cycle.note && <div className="mb-4" />}
                  <ul className="space-y-2.5 mb-6">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check size={14} className="text-emerald-400 mt-0.5 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => navigate("/login?mode=register")}
                    className={`w-full ${
                      p.highlight
                        ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                        : "bg-white/10 hover:bg-white/15 text-white"
                    } font-semibold`}
                    data-testid={`landing-plan-cta-${p.key}`}
                  >
                    {p.cta}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-5 bg-slate-900/30">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">
              Domande frequenti
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">
              Tutto quello che vuoi sapere.
            </h2>
          </div>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <button
                key={i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full text-left p-5 rounded-xl bg-white/[0.04] border border-white/[0.06] hover:border-white/10 transition"
                data-testid={`landing-faq-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-medium text-base">{f.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-slate-500 transition-transform shrink-0 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </div>
                {openFaq === i && (
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{f.a}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-5">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-emerald-500/5 to-transparent blur-3xl rounded-full pointer-events-none" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-tight mb-5">
              Pronto a fischiare il calcio d&apos;inizio?
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-8">
              Configura la tua società in 2 minuti con il setup guidato.
              Funziona da subito, anche se la prima squadra arriva domani.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate("/login?mode=register")}
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 h-12 px-7 text-base font-semibold shadow-[0_0_30px_-8px_rgba(16,185,129,0.6)]"
                data-testid="landing-final-cta"
              >
                Inizia gratis ora
                <ArrowRight size={18} className="ml-1.5" />
              </Button>
              <Button
                onClick={() => setDemoOpen(true)}
                size="lg"
                variant="outline"
                className="h-12 px-7 text-base font-semibold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              >
                Richiedi demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 px-5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Trophy size={14} className="text-emerald-300" />
            </div>
            <span>Calcio Club Manager · &copy; 2026</span>
          </div>
          <div className="flex items-center gap-5 flex-wrap justify-center">
            <a href="#features" className="hover:text-slate-300">Funzionalità</a>
            <a href="#pricing" className="hover:text-slate-300">Prezzi</a>
            <Link to="/legal/trust" className="hover:text-emerald-400 inline-flex items-center gap-1" data-testid="landing-trust-link">
              <ShieldCheck size={12} /> Trust Center
            </Link>
            <Link to="/login" className="hover:text-slate-300">Accedi</Link>
          </div>
        </div>
      </footer>

      {/* Demo request dialog */}
      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}

function DemoRequestDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", club: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nome ed email sono obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/demo-request", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        club: form.club.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      });
      setDone(true);
      toast.success("Richiesta ricevuta! Ti contatteremo entro 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) {
          // reset after close
          setTimeout(() => {
            setDone(false);
            setForm({ name: "", email: "", club: "", phone: "", notes: "" });
          }, 300);
        }
      }}
    >
      <DialogContent
        className="sm:max-w-md bg-slate-900 border-white/10 text-slate-100"
        data-testid="landing-demo-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">
            {done ? "Richiesta ricevuta!" : "Richiedi una demo"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {done
              ? "Ti contatteremo entro 24h all'email indicata."
              : "Compila il form: un Mister del nostro team ti contatterà entro 24h."}
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-6 text-center">
            <div className="h-14 w-14 rounded-full bg-emerald-500/20 mx-auto flex items-center justify-center mb-3">
              <Check className="text-emerald-400" size={28} />
            </div>
            <p className="text-sm text-slate-300">
              Nel frattempo, perché non provi gratis l&apos;app?
            </p>
            <div className="mt-4">
              <Link to="/login?mode=register">
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
                  Crea account gratuito
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-slate-300">Nome e cognome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-slate-800 border-white/10 text-white"
                placeholder="Mister Andrea Bianchi"
                data-testid="landing-demo-name"
                required
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-slate-800 border-white/10 text-white"
                placeholder="mister@asd.it"
                data-testid="landing-demo-email"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Società</Label>
                <Input
                  value={form.club}
                  onChange={(e) => setForm({ ...form, club: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                  placeholder="ASD Aurora"
                />
              </div>
              <div>
                <Label className="text-slate-300">Telefono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                  placeholder="+39…"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Note (opzionale)</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 rounded-md bg-slate-800 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Quante squadre? Quanti giocatori? Esigenze particolari?"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              data-testid="landing-demo-submit"
            >
              {submitting ? "Invio…" : (
                <>
                  <Send size={14} className="mr-1.5" /> Invia richiesta
                </>
              )}
            </Button>
            <div className="flex items-center justify-center gap-4 text-[10px] text-slate-500 pt-1">
              <span className="inline-flex items-center gap-1">
                <Mail size={10} /> Risposta entro 24h
              </span>
              <span className="inline-flex items-center gap-1">
                <Phone size={10} /> Nessuno spam
              </span>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
